import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap, Observable, of, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _titleService = inject(Title);
  private _metaService = inject(Meta);

  private defaultTitle = 'mogenius | Your Kubernetes Manager for 10x Productivity';
  private defaultDescription =
    'mogenius simplifies Kubernetes, empowering dev teams to deploy, monitor, and manage environments effortlessly with GitOps workflows, RBAC, developer self-service, and more.';
  private defaultImage = 'https://app.mogenius.com/assets/images/artboard.jpg';
  private _routerSubscription: Subscription | null = null;

  public initialize(): Observable<any> {
    if (this._routerSubscription) {
      return of(true);
    }

    this._routerSubscription = this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this._activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        const title = this.generateTitle(data?.meta?.['title']);
        const description = data?.meta?.['description'] || this.defaultDescription;
        const image = data?.meta?.['image'] || this.defaultImage;

        this._titleService.setTitle(title);
        this._metaService.updateTag({
          name: 'description',
          content: description
        });
        this._metaService.updateTag({
          property: 'og:title',
          content: title
        });
        this._metaService.updateTag({
          property: 'og:description',
          content: description
        });
        this._metaService.updateTag({
          property: 'og:image',
          content: image
        });
        this._metaService.updateTag({
          property: 'og:url',
          content: window.location.href
        });
        this._metaService.updateTag({
          name: 'twitter:title',
          content: title
        });
        this._metaService.updateTag({
          name: 'twitter:description',
          content: description
        });
        this._metaService.updateTag({
          name: 'twitter:image',
          content: image
        });
        this._metaService.updateTag({
          name: 'twitter:card',
          content: 'summary_large_image'
        });
      });

    return of(true);
  }

  private generateTitle(title?: string): string {
    if (!title) {
      return this.defaultTitle;
    }
    return `mogenius | ${title}`;
  }
}
